
import React, { useState } from 'react';
import { useConnectionRequests } from '@/hooks/useConnectionRequests';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { formatDistanceToNow } from 'date-fns';
import { CheckCircle, XCircle, Clock, Mail } from 'lucide-react';

const ConnectionRequestsManagement = () => {
  const { requests, isLoading, approveRequest, rejectRequest } = useConnectionRequests();

  const pendingRequests = requests.filter(req => req.status === 'pending');
  const processedRequests = requests.filter(req => req.status !== 'pending');

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return <Badge variant="secondary"><Clock className="w-3 h-3 mr-1" />Pending</Badge>;
      case 'approved':
        return <Badge variant="default" className="bg-green-500"><CheckCircle className="w-3 h-3 mr-1" />Approved</Badge>;
      case 'rejected':
        return <Badge variant="destructive"><XCircle className="w-3 h-3 mr-1" />Rejected</Badge>;
      case 'contacted':
        return <Badge variant="outline"><Mail className="w-3 h-3 mr-1" />Contacted</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        <h2 className="text-xl font-medium">Connection Requests</h2>
        <p className="text-gray-600">Loading connection requests...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-medium">Connection Requests</h2>
        <div className="flex gap-2">
          <Badge variant="secondary">{pendingRequests.length} Pending</Badge>
          <Badge variant="outline">{processedRequests.length} Processed</Badge>
        </div>
      </div>

      <Tabs defaultValue="pending" className="space-y-4">
        <TabsList>
          <TabsTrigger value="pending">
            Pending ({pendingRequests.length})
          </TabsTrigger>
          <TabsTrigger value="processed">
            Processed ({processedRequests.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="pending" className="space-y-4">
          {pendingRequests.length === 0 ? (
            <Card>
              <CardContent className="pt-6">
                <p className="text-center text-gray-500">No pending connection requests.</p>
              </CardContent>
            </Card>
          ) : (
            pendingRequests.map((request) => (
              <Card key={request.id}>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg">
                      Request for: {request.review?.company} - {request.review?.job_title}
                    </CardTitle>
                    {getStatusBadge(request.status)}
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm font-medium text-gray-700">Requester Email:</p>
                      <p className="text-sm">{request.requester_email}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-700">Review Reference:</p>
                      <p className="text-sm font-mono">{request.review?.unique_ref}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-700">Requested:</p>
                      <p className="text-sm">{formatDistanceToNow(new Date(request.created_at), { addSuffix: true })}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-700">Terms Agreed:</p>
                      <p className="text-sm">{request.terms_agreed ? 'Yes' : 'No'}</p>
                    </div>
                  </div>
                  
                  <div className="flex gap-2 pt-4">
                    <Button
                      onClick={() => approveRequest(request.id)}
                      className="bg-green-600 hover:bg-green-700"
                    >
                      <CheckCircle className="w-4 h-4 mr-1" />
                      Approve
                    </Button>
                    <Button
                      onClick={() => rejectRequest(request.id)}
                      variant="destructive"
                    >
                      <XCircle className="w-4 h-4 mr-1" />
                      Reject
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </TabsContent>

        <TabsContent value="processed" className="space-y-4">
          {processedRequests.length === 0 ? (
            <Card>
              <CardContent className="pt-6">
                <p className="text-center text-gray-500">No processed connection requests.</p>
              </CardContent>
            </Card>
          ) : (
            processedRequests.map((request) => (
              <Card key={request.id}>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg">
                      Request for: {request.review?.company} - {request.review?.job_title}
                    </CardTitle>
                    {getStatusBadge(request.status)}
                  </div>
                </CardHeader>
                <CardContent className="space-y-2">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm font-medium text-gray-700">Requester Email:</p>
                      <p className="text-sm">{request.requester_email}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-700">Review Reference:</p>
                      <p className="text-sm font-mono">{request.review?.unique_ref}</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-700">Processed:</p>
                      <p className="text-sm">{formatDistanceToNow(new Date(request.updated_at), { addSuffix: true })}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ConnectionRequestsManagement;
